<script setup lang="ts">
import { PhClock, PhPushPin, PhShieldCheck } from "@phosphor-icons/vue"
import type { TableColumn, TableRow } from "@nuxt/ui"
import { computed, reactive, ref } from "vue"
import ClippedValue from "../components/ClippedValue.vue"
import CopyButton from "../components/CopyButton.vue"
import DataState from "../components/DataState.vue"
import DetailPanel from "../components/DetailPanel.vue"
import FilterBar from "../components/FilterBar.vue"
import FilterSearch from "../components/FilterSearch.vue"
import FilterSelect from "../components/FilterSelect.vue"
import MetricTile from "../components/MetricTile.vue"
import NullBadge from "../components/NullBadge.vue"
import PageHeader from "../components/PageHeader.vue"
import SessionId from "../components/SessionId.vue"
import SortableTable from "../components/SortableTable.vue"
import StatusBadge from "../components/StatusBadge.vue"
import DashboardLayout from "../layouts/DashboardLayout.vue"
import { useDashboardStore } from "../stores/dashboard"
import type { ActiveConnection, ConnectionStatus } from "../utils/dashboard-types"
import { formatBytes, formatDuration, formatNumber, formatRelative } from "../utils/format"

type TableSorting = Array<{ id: string; desc: boolean }>
type ConnectionFilters = {
  country: string
  mode: "all" | "strict" | "sticky"
  protocol: "all" | "tcp" | "udp"
  search: string
  status: "all" | ConnectionStatus
}

const dashboard = useDashboardStore()
const selectedConnectionId = ref("")
const connectionSorting = ref<TableSorting>([{ id: "lastEventAt", desc: true }])
const defaultFilters: ConnectionFilters = {
  country: "all",
  mode: "all",
  protocol: "all",
  search: "",
  status: "all"
}
const filters = reactive<ConnectionFilters>({ ...defaultFilters })

const statusItems = [
  { label: "All statuses", value: "all" },
  { label: "Active", value: "active" },
  { label: "Warming", value: "warming" },
  { label: "Terminated", value: "paused" }
]
const modeItems = [
  { label: "All modes", value: "all" },
  { label: "Strict", value: "strict" },
  { label: "Sticky", value: "sticky" }
]
const protocolItems = [
  { label: "All protocols", value: "all" },
  { label: "TCP", value: "tcp" },
  { label: "UDP", value: "udp" }
]
const countryItems = computed(() => [
  { label: "All countries", value: "all" },
  ...Array.from(
    new Set(
      dashboard.connections
        .map((connection) => connection.country)
        .filter((country): country is string => Boolean(country))
    )
  )
    .sort()
    .map((country) => ({ label: country, value: country }))
])
const filteredConnections = computed(() => {
  const search = filters.search.trim().toLowerCase()

  return dashboard.connections.filter((connection) => {
    const matchesSearch =
      !search ||
      [
        connection.id,
        connection.sessionId,
        connection.clientId,
        connection.nodeId,
        connection.target,
        connection.edgeRegion
      ]
        .join(" ")
        .toLowerCase()
        .includes(search)
    const matchesCountry = filters.country === "all" || connection.country === filters.country
    const matchesMode =
      filters.mode === "all" ||
      (filters.mode === "strict" && connection.strict) ||
      (filters.mode === "sticky" && connection.sticky)
    const matchesProtocol = filters.protocol === "all" || connection.protocol === filters.protocol
    const matchesStatus = filters.status === "all" || connection.status === filters.status

    return matchesSearch && matchesCountry && matchesMode && matchesProtocol && matchesStatus
  })
})
const filtersDirty = computed(() =>
  Object.entries(defaultFilters).some(([key, value]) => filters[key as keyof ConnectionFilters] !== value)
)
const selectedConnection = computed(
  () =>
    filteredConnections.value.find((connection) => connection.id === selectedConnectionId.value) ??
    filteredConnections.value[0]
)
const strictCount = computed(() => dashboard.connections.filter((connection) => connection.strict === true).length)
const stickyCount = computed(() => dashboard.connections.filter((connection) => connection.sticky === true).length)
const udpCount = computed(() => dashboard.connections.filter((connection) => connection.protocol === "udp").length)
const terminatedCount = computed(
  () => dashboard.connections.filter((connection) => connection.status === "paused").length
)
const connectionMissingTooltips = {
  bytes: "Byte counters need transport metrics linked to this client row.",
  country: "No matching session country was retained for this lifecycle event.",
  duration: "Duration is only derived for rows whose latest event is client_connected.",
  node: "The client lifecycle event did not include a nodeId.",
  protocol: "Client lifecycle rows do not yet carry TCP or UDP protocol metadata.",
  target: "Target host is only available when transport telemetry emits it."
}
const connectionColumns: TableColumn<ActiveConnection>[] = [
  {
    accessorKey: "clientId",
    header: "Client",
    meta: {
      class: {
        td: "min-w-[170px]"
      }
    }
  },
  {
    accessorKey: "status",
    header: "Status"
  },
  {
    accessorKey: "sessionId",
    header: "Session"
  },
  {
    accessorKey: "nodeId",
    header: "Node"
  },
  {
    accessorKey: "country",
    header: "Country"
  },
  {
    accessorKey: "protocol",
    header: "Protocol"
  },
  {
    accessorKey: "target",
    header: "Target"
  },
  {
    accessorKey: "durationSeconds",
    header: "Duration"
  },
  {
    accessorKey: "rxBytes",
    header: "Received"
  },
  {
    accessorKey: "txBytes",
    header: "Sent"
  },
  {
    accessorKey: "lastEventAt",
    header: "Last event"
  }
]
const connectionTableMeta = computed(() => ({
  class: {
    tr: (row: TableRow<ActiveConnection>) =>
      row.original.id === selectedConnection.value?.id
        ? "dashboard-table-row--selected cursor-pointer"
        : "cursor-pointer"
  }
}))

function selectConnection(connection: ActiveConnection) {
  selectedConnectionId.value = connection.id
}

function selectConnectionRow(_event: Event, row: TableRow<ActiveConnection>) {
  selectConnection(row.original)
}

function resetFilters() {
  Object.assign(filters, defaultFilters)
}
</script>

<template>
  <DashboardLayout>
    <div class="dashboard-data-page flex min-w-0 flex-col gap-6">
      <PageHeader
        title="Connection activity"
        description="Client lifecycle events and filters."
        details="Includes session, node, strict, sticky, protocol, target, byte, duration, and country context when the source events emit it."
      >
        <template #actions>
          <span class="dashboard-mono text-sm text-(--dash-muted)">
            {{ formatNumber(dashboard.connections.length) }}
          </span>
        </template>
      </PageHeader>

      <DataState
        :loading="dashboard.isLoading"
        :error="dashboard.errorMessage"
        :empty="dashboard.connections.length === 0"
        empty-title="No connection activity"
        empty-description="No client lifecycle events were returned by the Worker dashboard API."
      >
        <section class="dashboard-data-content flex min-w-0 flex-1 flex-col gap-4">
          <section class="grid gap-3 md:grid-cols-2 xl:grid-cols-4">
            <MetricTile
              label="Open rows"
              :value="formatNumber(dashboard.connections.length - terminatedCount)"
              tone="success"
              caption="Latest event is connected or resumed"
            />
            <MetricTile
              label="Strict"
              :value="formatNumber(strictCount)"
              tone="neutral"
              caption="Session requires requested country"
            />
            <MetricTile
              label="Sticky"
              :value="formatNumber(stickyCount)"
              tone="neutral"
              caption="Session has sticky assignment input"
            />
            <MetricTile
              label="UDP assoc."
              :value="formatNumber(udpCount)"
              tone="neutral"
              caption="SOCKS UDP associations"
            />
          </section>

          <FilterBar
            :count-label="formatNumber(filteredConnections.length)"
            :dirty="filtersDirty"
            @reset="resetFilters"
          >
            <template #search>
              <FilterSearch
                v-model="filters.search"
                data-testid="connection-search"
                label="Search connections"
                placeholder="Search connection"
                size="xs"
              />
            </template>
            <template #filters>
              <div class="grid min-w-0 gap-2 sm:grid-cols-2 xl:grid-cols-[repeat(4,minmax(10.5rem,1fr))]">
                <FilterSelect v-model="filters.status" :items="statusItems" label="Connection status" size="xs" />
                <FilterSelect v-model="filters.mode" :items="modeItems" label="Connection mode" size="xs" />
                <FilterSelect v-model="filters.protocol" :items="protocolItems" label="Connection protocol" size="xs" />
                <FilterSelect v-model="filters.country" :items="countryItems" label="Connection country" size="xs" />
              </div>
            </template>
          </FilterBar>

          <div class="dashboard-data-workspace grid min-w-0 flex-1 gap-4 xl:grid-cols-[minmax(0,1fr)_380px]">
            <div class="dashboard-data-table-shell min-w-0">
              <SortableTable
                v-model:sorting="connectionSorting"
                :columns="connectionColumns"
                :data="filteredConnections"
                :meta="connectionTableMeta"
                title="Connection activity"
                eyebrow="Client lifecycle"
                empty-title="No matching connections"
                empty-description="Relax the filters to show more connection activity."
                modal-description="Client lifecycle rows with session, node, country, protocol, byte, and duration context."
                :ui="{ root: 'dashboard-table min-w-[1320px]', th: 'whitespace-nowrap', td: 'whitespace-nowrap' }"
                sticky="header"
                @select="selectConnectionRow"
              >
                <template #status-cell="{ row }">
                  <StatusBadge
                    v-if="row.original.status === 'paused'"
                    status="paused"
                    label="Terminated"
                    tooltip="Latest lifecycle event was client_paused or client_disconnected."
                  />
                  <StatusBadge v-else :status="row.original.status" />
                </template>
                <template #clientId-cell="{ row }">
                  <ClippedValue :value="row.original.clientId" max-class="max-w-[11rem]" title="Client id" />
                </template>
                <template #sessionId-cell="{ row }">
                  <div class="flex min-w-0 items-center gap-2">
                    <SessionId
                      v-if="row.original.sessionId !== 'not-emitted'"
                      :value="row.original.sessionId"
                      max-class="max-w-[8rem]"
                    />
                    <NullBadge v-else field="session" />
                    <CopyButton :value="row.original.sessionId" label="Copy session id" />
                  </div>
                </template>
                <template #nodeId-cell="{ row }">
                  <ClippedValue
                    v-if="row.original.nodeId"
                    :value="row.original.nodeId"
                    max-class="max-w-[11rem]"
                    title="Node id"
                  />
                  <NullBadge v-else field="node" :tooltip="connectionMissingTooltips.node" />
                </template>
                <template #country-cell="{ row }">
                  <span v-if="row.original.country" class="dashboard-mono">{{ row.original.country }}</span>
                  <NullBadge v-else field="country" :tooltip="connectionMissingTooltips.country" />
                </template>
                <template #protocol-cell="{ row }">
                  <StatusBadge v-if="row.original.protocol" :status="row.original.protocol" />
                  <NullBadge v-else field="protocol" :tooltip="connectionMissingTooltips.protocol" />
                </template>
                <template #target-cell="{ row }">
                  <ClippedValue
                    v-if="row.original.target ?? row.original.lastEventType"
                    :value="row.original.target ?? row.original.lastEventType ?? ''"
                    max-class="max-w-[18rem]"
                    title="Target"
                  />
                  <NullBadge v-else field="target" :tooltip="connectionMissingTooltips.target" />
                </template>
                <template #durationSeconds-cell="{ row }">
                  <span v-if="row.original.durationSeconds !== null" class="dashboard-mono">
                    {{ formatDuration(row.original.durationSeconds) }}
                  </span>
                  <NullBadge v-else field="duration" :tooltip="connectionMissingTooltips.duration" />
                </template>
                <template #rxBytes-cell="{ row }">
                  <span v-if="row.original.rxBytes !== null" class="dashboard-mono">{{
                    formatBytes(row.original.rxBytes)
                  }}</span>
                  <NullBadge v-else field="received bytes" :tooltip="connectionMissingTooltips.bytes" />
                </template>
                <template #txBytes-cell="{ row }">
                  <span v-if="row.original.txBytes !== null" class="dashboard-mono">{{
                    formatBytes(row.original.txBytes)
                  }}</span>
                  <NullBadge v-else field="sent bytes" :tooltip="connectionMissingTooltips.bytes" />
                </template>
                <template #lastEventAt-cell="{ row }">
                  <div class="flex items-center gap-2">
                    <span>{{ formatRelative(row.original.lastEventAt ?? dashboard.summary.generatedAt) }}</span>
                    <StatusBadge v-if="row.original.strict" status="strict" />
                    <StatusBadge v-if="row.original.sticky" status="sticky" />
                  </div>
                </template>
              </SortableTable>
            </div>

            <Transition name="panel-fade" mode="out-in">
              <DetailPanel
                v-if="selectedConnection"
                :key="selectedConnection.id"
                :status="selectedConnection.status"
                :subtitle="selectedConnection.target ?? selectedConnection.lastEventType ?? 'target unavailable'"
                :title="selectedConnection.id"
              >
                <div class="grid gap-3 text-sm">
                  <div class="flex items-center justify-between gap-3">
                    <span class="text-(--dash-muted)">Session</span>
                    <SessionId :value="selectedConnection.sessionId" max-class="max-w-[12rem]" />
                  </div>
                  <div class="flex items-center justify-between gap-3">
                    <span class="text-(--dash-muted)">Client</span>
                    <span class="dashboard-mono text-(--dash-ink)">{{ selectedConnection.clientId }}</span>
                  </div>
                  <div class="flex items-center justify-between gap-3">
                    <span class="text-(--dash-muted)">Node</span>
                    <span v-if="selectedConnection.nodeId" class="dashboard-mono text-(--dash-ink)">
                      {{ selectedConnection.nodeId }}
                    </span>
                    <NullBadge v-else field="node" :tooltip="connectionMissingTooltips.node" />
                  </div>
                  <div class="flex items-center justify-between gap-3">
                    <span class="text-(--dash-muted)">Received</span>
                    <span v-if="selectedConnection.rxBytes !== null" class="dashboard-mono text-(--dash-ink)">
                      {{ formatBytes(selectedConnection.rxBytes) }}
                    </span>
                    <NullBadge v-else field="received bytes" :tooltip="connectionMissingTooltips.bytes" />
                  </div>
                  <div class="flex items-center justify-between gap-3">
                    <span class="text-(--dash-muted)">Sent</span>
                    <span v-if="selectedConnection.txBytes !== null" class="dashboard-mono text-(--dash-ink)">
                      {{ formatBytes(selectedConnection.txBytes) }}
                    </span>
                    <NullBadge v-else field="sent bytes" :tooltip="connectionMissingTooltips.bytes" />
                  </div>
                </div>

                <template #footer>
                  <div class="flex flex-wrap items-center gap-2 text-xs text-(--dash-muted)">
                    <span class="inline-flex items-center gap-1">
                      <PhShieldCheck :size="15" />
                      strict
                      <span v-if="selectedConnection.strict !== null">{{
                        selectedConnection.strict ? "on" : "off"
                      }}</span>
                      <NullBadge v-else />
                    </span>
                    <span class="inline-flex items-center gap-1">
                      <PhPushPin :size="15" />
                      sticky
                      <span v-if="selectedConnection.sticky !== null">{{
                        selectedConnection.sticky ? "on" : "off"
                      }}</span>
                      <NullBadge v-else />
                    </span>
                    <span class="inline-flex items-center gap-1">
                      <PhClock :size="15" />
                      <span v-if="selectedConnection.durationSeconds !== null">
                        {{ formatDuration(selectedConnection.durationSeconds) }}
                      </span>
                      <NullBadge v-else field="duration" :tooltip="connectionMissingTooltips.duration" />
                    </span>
                  </div>
                </template>
              </DetailPanel>
            </Transition>
          </div>
        </section>
      </DataState>
    </div>
  </DashboardLayout>
</template>
